import { useFragment } from "@/generated";
import {
  AddProjectDocument,
  ProjectFieldsFragmentDoc,
  type ProjectInput,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for adding new projects with Apollo cache updates.
 *
 * Provides a mutation function to create one or more projects and updates
 * the `projectsByParentId` Apollo cache query after creation.
 *
 * Root projects (parentId = null) and child projects use the same
 * `projectsByParentId` query, so cache updates are handled through
 * this single query regardless of hierarchy level.
 *
 * Newly created projects are prepended to the cached list for immediate
 * visibility, and the total count is incremented accordingly.
 *
 * Supports creating linked copies when duplicating existing projects.
 *
 * @returns An object containing:
 *   - addProject: Async function to create projects. Takes ProjectInput and returns an array of created projects.
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { addProject, loading } = useAddProject();
 * const newProjects = await addProject({
 *   name: "New Project",
 *   productIds: ["product123"],
 *   parentId: null, // root project
 *   isPublic: true
 * });
 */

export function useAddProject() {
  const [addProjectMutation, { loading }] = useMutation(AddProjectDocument, {
    update(cache, { data }) {
      if (!data) return;

      // Unmasked all returned projects
      const unmaskedProjects = data.addProject.map((project) =>
        useFragment(ProjectFieldsFragmentDoc, project)
      );

      // Group projects by parentId
      const projectsByParentIdMap = new Map<string | null, typeof unmaskedProjects>();

      for (const project of unmaskedProjects) {
        const key = project.parentId ?? null;

        const projectsForParent = projectsByParentIdMap.get(key) ?? [];

        projectsForParent.push(project);
        projectsByParentIdMap.set(key, projectsForParent);
      }

      // Batch all cache modifications
      cache.batch({
        update(cache) {
          // Normalized cache
          for (const project of unmaskedProjects) {
            const id = cache.identify(project);
            if (id) {
              cache.writeFragment({
                id,
                fragment: ProjectFieldsFragmentDoc,
                data: project,
              });
            }
          }

          // Update each parentId field in ROOT_QUERY
          for (const [parentId, projects] of projectsByParentIdMap.entries()) {
            const newRefs = projects
              .map((p) => cache.identify(p))
              .filter(Boolean)
              .map((id) => ({ __ref: id }));

            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                projectsByParentId(existingData, { storeFieldName }) {
                  const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;
                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: [...newRefs, ...existingData.items],
                    total: (existingData.total || 0) + newRefs.length,
                  };
                },
              },
            });

            if (parentId !== null) {
              cache.modify({
                id: `Project:${parentId}`,
                fields: {
                  itemCount(existingData = 0) {
                    return existingData + newRefs.length;
                  },
                },
              });
            }
          }
        },
      });
    },
  });

  const addProject = async (input: ProjectInput, thumbnailFile?: File | null) => {
    return withToast(
      async () => {
        const result = await addProjectMutation({
          variables: { input, thumbnailFile: thumbnailFile || undefined },
        });

        const maskedProjects = result.data?.addProject ?? [];
        const newProjects = maskedProjects.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        return newProjects;
      },
      {
        loading: "Creating project...",
        success: `Successfully created project "${input.name}"`,
        error: "Failed to create projects. Please try again.",
      },
      "addProject"
    );
  };

  return {
    addProject,
    loading,
  };
}
